import type { KeywordTrendSeries } from "@/types/keyword-trends";

/**
 * AUT-429. The arithmetic behind the Keyword Trends screen, kept out of the page so it can be
 * tested at the level it belongs to.
 *
 * It lives here because of what jsdom cannot do: recharts measures its container, so the page's
 * Vitest suite mocks `ResponsiveContainer` and no assertion about a rendered chart reaches the
 * numbers feeding it. A review mutated the seasonal pivot four ways -- shifting every month by
 * three, flattening every year to zero -- and the page suite stayed green each time. Pure functions
 * with their own tests close that, per `conventions/05-testing-strategy.md`: a test that could pass
 * at a lower level should not be written at a higher one.
 */

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export interface WeeklyTotal {
  week: string;
  volume: number;
}

export interface KeywordRow {
  keyword: string;
  total: number;
  weeks: number;
  avg: number;
}

export interface GrowthWindow {
  label: string;
  days: number;
  current: number;
  prior: number;
  change: number | null;
}

export interface RangeBound {
  min: number | null;
  max: number | null;
}

const DAY_MS = 86_400_000;

const utc = (week: string) => new Date(`${week}T00:00:00Z`);

/** `1.24M` / `1.2K` / `847` — the screen's one number format, axis ticks and KPIs alike. */
export const formatVolume = (n: number): string =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : `${Math.round(n)}`;

/** `2026-08-02` -> `Aug 26`, so the continuous axis stays readable at 150+ points. */
export const formatWeekTick = (week: string): string => {
  const [y, m] = week.split("-");

  return `${MONTHS[Number(m) - 1] ?? ""} ${y.slice(2)}`;
};

export const inRange = (value: number, bound?: RangeBound): boolean =>
  !bound || ((bound.min == null || value >= bound.min) && (bound.max == null || value <= bound.max));

/** One point per week, summed across every keyword in the set, oldest first. */
export function weeklyTotals(keywords: KeywordTrendSeries[]): WeeklyTotal[] {
  const totals = new Map<string, number>();
  for (const k of keywords) {
    for (const p of k.points) totals.set(p.week, (totals.get(p.week) ?? 0) + p.volume);
  }

  return [...totals.entries()]
    .map(([week, volume]) => ({ week, volume }))
    .sort((a, b) => a.week.localeCompare(b.week));
}

/**
 * FR-4 year-over-year: twelve month rows, one numeric key per calendar year present.
 *
 * A month a year has no data for is `null`, never `0` — the chart must break the line there rather
 * than draw a trough nobody measured. Months are bucketed in **UTC**, matching how the weeks
 * arrive; local-time bucketing moves a week either side of a month boundary.
 */
/**
 * AUT-445: is the reading at `index` surrounded by gaps on both sides?
 *
 * A line needs two adjacent readings to draw a segment, so with `connectNulls={false}` and
 * `dot={false}` a reading whose neighbours are both missing renders **nothing at all** -- the value
 * is in the data and in the tooltip, but there is no mark on the chart. Those are the points that
 * get a dot.
 *
 * Systematic rather than rare on this screen: `seasonalOverlay` writes `null` for every month a
 * year has no data for, and the 156-week window opens mid-month about three years back, so the
 * earliest year routinely carries exactly one partial month.
 *
 * A single-reading series counts: the first and last rows have one neighbour, and a missing one on
 * the only side there is still leaves the point unconnected.
 */
export function isIsolatedPoint(
  rows: Array<Record<string, string | number | null>>,
  key: string,
  index: number,
): boolean {
  const has = (i: number) => i >= 0 && i < rows.length && typeof rows[i]?.[key] === "number";

  return has(index) && !has(index - 1) && !has(index + 1);
}

export function seasonalOverlay(totals: WeeklyTotal[]): {
  overlay: Array<Record<string, string | number | null>>;
  years: number[];
} {
  const byMonthYear = new Map<number, Map<number, number>>();
  const yearSet = new Set<number>();

  for (const { week, volume } of totals) {
    const d = utc(week);
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    yearSet.add(y);
    if (!byMonthYear.has(m)) byMonthYear.set(m, new Map());
    const row = byMonthYear.get(m)!;
    row.set(y, (row.get(y) ?? 0) + volume);
  }

  const years = [...yearSet].sort((a, b) => a - b);
  const overlay = MONTHS.map((month, m) => {
    const row: Record<string, string | number | null> = { month };
    for (const y of years) row[String(y)] = byMonthYear.get(m)?.get(y) ?? null;

    return row;
  });

  return { overlay, years };
}

/**
 * FR-6: each window's total against the equally-long window immediately before it, anchored to the
 * latest week in the data rather than to today.
 *
 * A window with no preceding data reports `change: null` — not `0`, and not `100`. The windows are
 * inclusive of both endpoints, which is why the span is `(days - 1)`.
 */
export function growthWindows(totals: WeeklyTotal[]): GrowthWindow[] {
  const windows = [
    { label: "Last 90 days", days: 90 },
    { label: "Last 180 days", days: 180 },
    { label: "Last 365 days", days: 365 },
  ];

  if (!totals.length) {
    return windows.map((w) => ({ ...w, current: 0, prior: 0, change: null }));
  }

  const latest = utc(totals[totals.length - 1].week).getTime();

  return windows.map((w) => {
    const curStart = latest - (w.days - 1) * DAY_MS;
    const priorEnd = curStart - DAY_MS;
    const priorStart = priorEnd - (w.days - 1) * DAY_MS;

    let current = 0;
    let prior = 0;
    for (const { week, volume } of totals) {
      const t = utc(week).getTime();
      if (t >= curStart && t <= latest) current += volume;
      else if (t >= priorStart && t <= priorEnd) prior += volume;
    }

    return { ...w, current, prior, change: prior > 0 ? ((current - prior) / prior) * 100 : null };
  });
}

/** One row per keyword, largest total first — the table's natural order. */
export function keywordRows(keywords: KeywordTrendSeries[]): KeywordRow[] {
  return keywords
    .map((k) => {
      const total = k.points.reduce((s, p) => s + p.volume, 0);

      return { keyword: k.keyword, total, weeks: k.points.length, avg: k.points.length ? total / k.points.length : 0 };
    })
    .sort((a, b) => b.total - a.total);
}

/**
 * FR-7: narrowing is client-side over the set already returned — it must never re-query the
 * warehouse. `exclude` takes a comma- or newline-separated list; a row matching any of them is out.
 */
export function filterKeywordRows(
  rows: KeywordRow[],
  opts: {
    search?: string;
    contains?: string;
    exclude?: string;
    totalVolume?: RangeBound;
    weeks?: RangeBound;
  },
): KeywordRow[] {
  const q = (opts.search ?? "").trim().toLowerCase();
  const contains = (opts.contains ?? "").trim().toLowerCase();
  const excludes = (opts.exclude ?? "")
    .split(/[,\n]/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

  return rows.filter((r) => {
    const kw = r.keyword.toLowerCase();
    if (q && !kw.includes(q)) return false;
    if (contains && !kw.includes(contains)) return false;
    if (excludes.some((t) => kw.includes(t))) return false;
    if (!inRange(r.total, opts.totalVolume)) return false;
    if (!inRange(r.weeks, opts.weeks)) return false;

    return true;
  });
}
